<template>
  <div v-if="isPrint">
    <span class="print-cell" :class="[textAlign ? `text-${textAlign.toLowerCase()}` : '']">{{ inputValue }}&nbsp;</span>
  </div>
  <div v-else class="relative">
    <vs-input
      ref="mlInput"
      class="ml-input w-full"
      :class="textAlign"
      v-model="inputValue"
      :type="type"
      :readonly="readonly"
      :disabled="disabled"
      :icon-pack="iconPack"
      :icon="icon"
      :placeholder="placeholder"
      :size="`${size} ${alignClass}`"
      :danger="danger"
      :danger-text="dangerText"
      :success="success"
      :success-text="successText"
      @keyup="(e) => handleKeyup(e)"
      @click="(e) => $emit('click', e)"
      @focusout="(e) => handleFocusout(e)"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <!-- Clear 버튼 -->
    <div v-if="showClearButton" class="clear-button" @click="clearInput">
      <i class="material-icons">clear</i>
    </div>

    <div
      v-if="!readonly && !disabled && visibleCounter && isInputSelected"
      :class="['vs-textarea--count', 'counter', { negative: isExceeded, shake: isExceeded /* 글자 수 에러 표시 */ }]"
      style="bottom: 1px; padding: 0 6px 1px; line-height: 1rem"
    >
      {{ counterLength }} / {{ max }}
    </div>
  </div>
</template>

<script>
// 객체/배열이 value 로 넘어오는 것은 바인딩 실수다.
// 그대로 두면 toString() 되어 "[object Object]" 가 원본 데이터를 덮어쓰므로, 이 경우 입력값을 다루지 않는다.
const isPrimitiveValue = (value) => value === null || value === undefined || typeof value !== "object";

export default {
  name: "MaxLengthInput",
  props: {
    defaultValue: { type: [String, Number], default: "" },
    type: { type: String, default: "text" },
    value: { type: [String, Number], default: "" },
    placeholder: String,
    readonly: Boolean,
    disabled: Boolean,
    max: { type: Number, default: 200 },
    iconPack: String,
    icon: String,
    noneCount: String,
    minValue: Number,
    maxValue: Number,
    // input 하단에 현재 카운팅현황(0/max) 표기를 보이게 합니다.
    visibleCounter: { type: Boolean, default: false },
    size: String,
    // textAlign: left, center, right
    textAlign: {
      type: String,
      default: "left",
    },
    // 인쇄 모드일 경우, true 전달
    isPrint: {
      type: Boolean,
      default: false,
    },
    danger: Boolean,
    dangerText: String,
    success: Boolean,
    successText: String,
    // 제거하는 문자들
    removeChars: { type: Array, default: () => [] },
    // 값을 지울 수 있는 clear 버튼 표시 여부
    clearable: { type: Boolean, default: false },
  },
  data() {
    return {
      inputValue: isPrimitiveValue(this.value) ? this.value || this.defaultValue : "",
      // value 가 객체/배열이면 입력값을 부모로 되돌려보내지 않는다. (원본 데이터 훼손 방지)
      ignoreValue: !isPrimitiveValue(this.value),
      isExceeded: false,
      isInputSelected: false,
    };
  },
  computed: {
    alignClass() {
      return this.textAlign ? `text-${this.textAlign.toLowerCase()}` : "";
    },
    counterLength() {
      return this.inputValue.replaceAll(this.noneCount, "").length;
    },
    showClearButton() {
      return this.clearable && !this.readonly && !this.disabled && this.inputValue && this.inputValue.toString().length > 0;
    },
  },
  watch: {
    value: {
      handler(value) {
        if (!isPrimitiveValue(value)) {
          this.ignoreValue = true;
          return;
        }
        this.ignoreValue = false;
        this.inputValue = (value || "").toString();
      },
    },
    inputValue: {
      immediate: true,
      deep: true,
      handler(value) {
        if (this.ignoreValue) return;

        // value에서 noneCount 문자를 모두 제거
        if (this.noneCount) value = value.replaceAll(this.noneCount, "");

        /**
         * 최대 글자 수 검증 적용
         * max 값이 설정되어 있고, 현재 입력된 글자 수가 max 길이 만큼을 초과하면,
         * 초과하기 전의 값을 유지하고, 초과했음을 알리는 isExceeded를 true로 설정합니다.
         * isExceeded 값이 true로 변하면, 카운터의 색상이 변경되고, 진동 애니메이션이 발생합니다.
         */
        if (this.max && value.length > this.max) {
          this.inputValue = this.value;
          this.isExceeded = true;

          setTimeout(() => {
            this.isExceeded = false;
          }, 300);
        } else if (0 < this.removeChars.length) {
          const setValue = value
            .split("")
            .filter((char) => !this.removeChars.includes(char))
            .join("");

          this.$emit("input", setValue);
          this.$emit("change", setValue);
        } else {
          this.$emit("input", value);
          this.$emit("change", value);
        }
      },
    },
  },
  methods: {
    handleFocus(event) {
      this.isInputSelected = true;
      this.$emit("focus", event);
    },
    handleBlur(event) {
      this.isInputSelected = false;
      this.$emit("blur", event);
    },
    handleFocusout(event) {
      if (this.type === "number") {
        let value = Number(this.inputValue);
        if (this.minValue || this.minValue === 0) {
          if (value < this.minValue) {
            value = this.minValue;
          }
        }
        if (this.maxValue || this.maxValue === 0) {
          if (value > this.maxValue) {
            value = this.maxValue;
          }
        }

        this.inputValue = value.toString();
      }
      this.$emit("focusout", event);
    },
    handleKeyup(event) {
      if (this.type === "number") {
        // 숫자 입력 시, 숫자가 아닌 문자를 제거합니다.
        this.inputValue = this.inputValue.replace(/[^0-9.-]/g, "");
      }
      this.$emit("keyup", event);
    },
    clearInput() {
      this.inputValue = "";
      this.$emit("input", "");
      this.$emit("change", "");
      this.$emit("clear");
      // 입력창에 포커스 유지
      this.$nextTick(() => {
        if (this.$refs.mlInput && this.$refs.mlInput.$el) {
          const input = this.$refs.mlInput.$el.querySelector("input");
          if (input) {
            input.focus();
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ml-input::v-deep {
  .input-span-placeholder {
    line-height: 1.2rem;
  }

  &.left input {
    text-align: left;
  }
  &.center input {
    text-align: center;
  }
  &.right input {
    text-align: right;
  }
}
.isFocus + .counter {
  opacity: 1;
}
.counter {
  transition: background-color 0.3s;
}
.counter.negative {
  background-color: #f44336;
}
@keyframes shake {
  0% {
    transform: translate(0, 100%);
  }
  10% {
    transform: translate(-3px, 100%);
  }
  20% {
    transform: translate(3px, 100%);
  }
  30% {
    transform: translate(-3px, 100%);
  }
  40% {
    transform: translate(3px, 100%);
  }
  50% {
    transform: translate(-3px, 100%);
  }
  60% {
    transform: translate(3px, 100%);
  }
  70% {
    transform: translate(-3px, 100%);
  }
  80% {
    transform: translate(3px, 100%);
  }
  90% {
    transform: translate(-3px, 100%);
  }
  100% {
    transform: translate(0, 100%);
  }
}

.counter.shake {
  animation: shake 0.3s;
}

.print-cell {
  display: block;
  width: 100%;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  padding: 7px 9px;
}

.clear-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s ease;
  z-index: 10;

  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  i {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    line-height: 1;
  }
}
</style>

<!-- placeholder 여백이 달라져 상단에 붙는 문제가 있어서 전체 style 로 추가 -->
<style>
.ml-input > .vs-con-input > .input-span-placeholder {
  padding: 0.7rem 1rem !important;
}

.ml-input > .vs-con-input > .vs-inputx[readonly] {
  background-color: #fafafa;
}
</style>
